import React, { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CommonInput from '../Components/CommonInput';
import CommonButton from '../Components/CommonButton';
import CommonLoader from '../Components/CommonLoader';
import CommonStepper from '../Components/CommonStepper';
import PageHeading from '../Components/PageHeading';
import StatusBadge from '../Components/StatusBadge';
import { SubHeading, Body } from '../Constants/Fonts';
import {
    add_sso_domain,
    create_sso_connection,
    update_sso_connection,
    verify_sso_domain_manually,
} from '../services';
import {
    CopyValue,
    Message,
    formatDate,
    formatSize,
    readData,
    useCopyValue,
    useSsoRequest,
} from './ssoShared';
import '../styles/ssoAuth.scss';

// The identity provider hands out a federation metadata XML — the SAML signing
// certificate is embedded in it, and that is what the create endpoint parses.
const METADATA_EXTENSIONS = ['.xml'];
const MAX_METADATA_SIZE_MB = 5;
const DOMAIN_REGEX = /^([a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;

const STEPS = [
    { step: 1, key: 'domain', title: 'Add Domain' },
    { step: 2, key: 'connection', title: 'Upload SAML Metadata' },
    { step: 3, key: 'verify', title: 'Verify Domain' },
    { step: 4, key: 'enable', title: 'Enable SSO' },
];

const INITIAL_FORM = {
    domain: '', name: '', metadataFile: null, note: '', metadataUrl: '', enabled: true,
};

// The wizard's primary action loads inside the CommonButton label.
const BtnLabel = ({ busy, children }) => (busy ? <CommonLoader size={16} variant='inverse' /> : children);

const StepCard = ({ step, title, description, done, children }) => (
    <div className={`sso_card mb-3 ${done ? 'is_done' : ''}`}>
        <div className='sso_card_head'>
            <span className={`sso_step_badge ${done ? 'done' : ''}`}>{done ? '✓' : step}</span>
            <div>
                <SubHeading color='var(--color-text-primary)' style={{ margin: '0 0 2px' }}>{title}</SubHeading>
                {description && <Body variant={2} style={{ margin: 0 }}>{description}</Body>}
            </div>
        </div>
        {children}
    </div>
);

const SummaryRow = ({ label, children }) => (
    <div className='sso_summary_row'>
        <span className='sso_label'>{label}</span>
        <Body as='span'>{children}</Body>
    </div>
);

function SsoAuthentication() {
    const navigate = useNavigate();
    const { busy, msg, say, clearMsg, request, toastNode } = useSsoRequest();
    const { copied, copy } = useCopyValue();

    const [form, setForm] = useState(INITIAL_FORM);
    // Records returned by each step — the next step reads its inputs off these
    // rather than asking the user to re-pick what was just created.
    const [domainRecord, setDomainRecord] = useState(null);
    const [connectionRecord, setConnectionRecord] = useState(null);
    const [finished, setFinished] = useState(false);
    const metadataInputRef = useRef(null);

    const setField = useCallback((key, value, stepKey) => {
        setForm((prev) => ({ ...prev, [key]: value }));
        if (stepKey) clearMsg(stepKey);
    }, [clearMsg]);

    // A step unlocks only once the one before it has produced its record.
    const step2Visible = Boolean(domainRecord);
    const step3Visible = Boolean(connectionRecord);
    const step4Visible = Boolean(domainRecord?.is_verified && connectionRecord);
    // 5 once saved, so the rail shows every step ticked off.
    const currentStep = finished ? 5 : (step4Visible ? 4 : step3Visible ? 3 : step2Visible ? 2 : 1);

    /* ---------------------------- Step 1 ---------------------------- */

    const handleAddDomain = () => {
        const domain = form.domain.trim().toLowerCase();
        if (!DOMAIN_REGEX.test(domain)) {
            return say('domain', 'error', domain ? 'Enter a valid domain, for example acme.com' : 'Please enter a domain.');
        }
        return request(
            'domain',
            () => add_sso_domain({ domain }),
            (res) => setDomainRecord(readData(res)),
            { loading: `Adding ${domain}…`, success: 'Domain added.' }
        );
    };

    /* ---------------------------- Step 2 ---------------------------- */

    const clearMetadataFile = useCallback(() => {
        setForm((prev) => ({ ...prev, metadataFile: null }));
        if (metadataInputRef.current) metadataInputRef.current.value = '';
    }, []);

    const handleMetadataChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return clearMetadataFile();

        const extension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
        if (!METADATA_EXTENSIONS.includes(extension)) {
            clearMetadataFile();
            return say('connection', 'error', 'Upload the identity provider federation metadata XML (.xml).');
        }
        if (file.size > MAX_METADATA_SIZE_MB * 1024 * 1024) {
            clearMetadataFile();
            return say('connection', 'error', `File must be smaller than ${MAX_METADATA_SIZE_MB} MB.`);
        }
        setField('metadataFile', file, 'connection');
    };

    const handleCreateConnection = () => {
        if (!form.name.trim()) return say('connection', 'error', 'Enter a name for this connection.');
        if (!form.metadataFile) return say('connection', 'error', 'Select the SAML metadata file.');

        const payload = new FormData();
        payload.append('name', form.name.trim());
        payload.append('domains', domainRecord?.domain);
        payload.append('metadata_file', form.metadataFile);

        return request(
            'connection',
            () => create_sso_connection(payload),
            (res) => {
                setConnectionRecord(readData(res));
                clearMetadataFile();
            },
            {
                loading: 'Reading the metadata and creating the connection…',
                success: 'Connection created. It stays disabled until step 4.',
            }
        );
    };

    /* ---------------------------- Step 3 ---------------------------- */

    const handleManualVerify = () => {
        if (!form.note.trim()) {
            return say('verify', 'error', 'Add a note explaining why this domain is accepted without DNS proof.');
        }
        return request(
            'verify',
            () => verify_sso_domain_manually(domainRecord?.id, { note: form.note.trim() }),
            (res) => setDomainRecord((prev) => ({ ...prev, ...(readData(res) || {}), is_verified: true })),
            { loading: `Verifying ${domainRecord?.domain}…`, success: 'Domain verified.' }
        );
    };

    /* ---------------------------- Step 4 ---------------------------- */

    const handleSaveConnection = () => {
        const url = form.metadataUrl.trim();
        if (url && !/^https?:\/\//i.test(url)) {
            return say('enable', 'error', 'Metadata URL must start with http:// or https://');
        }
        return request(
            'enable',
            () => update_sso_connection(connectionRecord?.id, {
                is_enabled: form.enabled,
                ...(url && { idp_metadata_url: url }),
            }),
            (res) => {
                setConnectionRecord((prev) => ({ ...prev, ...(readData(res) || {}), is_enabled: form.enabled }));
                setFinished(true);
            },
            {
                loading: form.enabled ? 'Enabling SSO…' : 'Saving the connection…',
                success: `SSO ${form.enabled ? 'enabled' : 'saved as disabled'} for ${domainRecord?.domain}.`,
            }
        );
    };

    const startOver = () => {
        setForm(INITIAL_FORM);
        setDomainRecord(null);
        setConnectionRecord(null);
        setFinished(false);
        clearMetadataFile();
    };

    return (
        <div className=' rp1_widget cmn_elevated_card sso_auth_wrapper  mt-2'>
            {toastNode}

            <PageHeading
                title='SSO Authentication'
                subtitle='Add a customer domain, upload their SAML metadata, verify the domain, then turn SSO on.'
                actions={(
                    <CommonButton
                        variant='secondary'
                        text='All SSO Domains'
                        onClick={() => navigate('/ssoAuth/domains')}
                    />
                )}
            />

            <div className='sso_stepper cmn_elevated_card'>
                <CommonStepper
                    steps={STEPS.map(({ key, title }) => ({ id: key, label: title }))}
                    currentIndex={currentStep - 1}
                />
            </div>

            {/* ---------------- Step 1 ---------------- */}
            <StepCard
                step={1}
                done={step2Visible}
                title='Add Domain'
                description={step2Visible
                    ? null
                    : 'Register the email domain your users will sign in with. Send the customer the TXT record to prove ownership, or accept it manually in step 3.'}
            >
                {step2Visible ? (
                    <div className='sso_summary '>
                        <SummaryRow label='Domain'>{domainRecord?.domain}</SummaryRow>
                        <SummaryRow label='Status'>
                            <StatusBadge
                                variant={domainRecord?.is_verified ? 'success' : 'warning'}
                                label={domainRecord?.is_verified ? 'Verified' : 'Pending verification'}
                            />
                        </SummaryRow>
                        {domainRecord?.txt_record?.value && (
                            <SummaryRow label='TXT Record'>
                                <CopyValue value={domainRecord.txt_record.value} copied={copied} onCopy={copy} />
                            </SummaryRow>
                        )}
                    </div>
                ) : (
                    <>
                        <div className='sso_add_domain_row'>
                            <div className='sso_domain_input'>
                                <CommonInput
                                    name='domain'
                                    value={form.domain}
                                    onChange={(e) => setField('domain', e.target.value.trim().toLowerCase(), 'domain')}
                                    placeholder='acme.com'
                                    maxLength={253}
                                />
                            </div>
                            <CommonButton
                                text={<BtnLabel busy={busy.domain}>Add Domain</BtnLabel>}
                                disable={busy.domain}
                                onClick={handleAddDomain}
                            />
                        </div>
                        <Message message={msg.domain} />
                    </>
                )}
            </StepCard>

            {/* ---------------- Step 2 ---------------- */}
            {step2Visible && (
                <StepCard
                    step={2}
                    done={step3Visible}
                    title='Upload SAML Metadata'
                    description={step3Visible
                        ? null
                        : `Upload the federation metadata XML from the identity provider for ${domainRecord?.domain} — the signing certificate is read out of it. The connection is created disabled.`}
                >
                    {step3Visible ? (
                        <div className='sso_summary'>
                            <SummaryRow label='Connection'>{connectionRecord?.name}</SummaryRow>
                            <SummaryRow label='Provider'>
                                {connectionRecord?.provider_label || connectionRecord?.provider || '-'}
                                {connectionRecord?.protocol ? ` · ${connectionRecord.protocol}` : ''}
                            </SummaryRow>
                            {connectionRecord?.idp_entity_id && (
                                <SummaryRow label='Issuer'>
                                    <span className='sso_wrap_value'>{connectionRecord.idp_entity_id}</span>
                                </SummaryRow>
                            )}
                            <SummaryRow label='Certificate Expiry'>
                                {formatDate(connectionRecord?.idp_cert_expires_at)}
                                {connectionRecord?.cert_days_remaining != null && (
                                    <Body as='span' variant={2}> ({connectionRecord.cert_days_remaining} days)</Body>
                                )}
                            </SummaryRow>
                        </div>
                    ) : (
                        <>
                            <div className='sso_form_grid'>
                                <div className='sso_field'>
                                    <label className='sso_label'>Connection Name</label>
                                    <CommonInput
                                        name='name'
                                        value={form.name}
                                        onChange={(e) => setField('name', e.target.value, 'connection')}
                                        placeholder='Acme Corp (Entra)'
                                        maxLength={100}
                                    />
                                </div>
                                <div className='sso_field'>
                                    <label className='sso_label'>Domain</label>
                                    <div className='sso_readonly_field'>{domainRecord?.domain}</div>
                                </div>
                            </div>

                            <div className='sso_field mt-3'>
                                <label className='sso_label'>SAML Metadata File</label>
                                <div className='sso_file_row'>
                                    <input
                                        ref={metadataInputRef}
                                        id='sso_metadata_file'
                                        type='file'
                                        className='sso_file_input'
                                        accept={METADATA_EXTENSIONS.join(',')}
                                        onChange={handleMetadataChange}
                                    />
                                    <label htmlFor='sso_metadata_file' className='sso_file_btn'>Choose File</label>
                                    {form.metadataFile ? (
                                        <span className='sso_file_chip'>
                                            {form.metadataFile.name}
                                            <Body as='span' variant={2}>({formatSize(form.metadataFile.size)})</Body>
                                            <button type='button' className='sso_remove_btn' onClick={clearMetadataFile}>✕</button>
                                        </span>
                                    ) : (
                                        <Body as='span' variant={2}>
                                            No file selected · federation metadata .xml up to {MAX_METADATA_SIZE_MB} MB
                                        </Body>
                                    )}
                                </div>
                            </div>

                            <Message message={msg.connection} />

                            <div className='sso_actions'>
                                <CommonButton
                                    text={<BtnLabel busy={busy.connection}>Create Connection</BtnLabel>}
                                    disable={busy.connection}
                                    onClick={handleCreateConnection}
                                />
                            </div>
                        </>
                    )}
                </StepCard>
            )}

            {/* ---------------- Step 3 ---------------- */}
            {step3Visible && (
                <StepCard
                    step={3}
                    done={step4Visible}
                    title='Verify Domain'
                    description={step4Visible
                        ? null
                        : `Accept ${domainRecord?.domain} without DNS proof — for domains we own, or provider-owned ones such as *.onmicrosoft.com that cannot host a TXT record. The note is stored on the record so an auditor can tell proven domains from asserted ones.`}
                >
                    {step4Visible ? (
                        <div className='sso_summary'>
                            <SummaryRow label='Domain'>
                                {domainRecord?.domain} <StatusBadge variant='success' label='Verified' />
                            </SummaryRow>
                            <SummaryRow label='Method'>
                                <span className='text-capitalize'>
                                    {domainRecord?.verification_method?.replace(/_/g, ' ') || 'manual'}
                                </span>
                            </SummaryRow>
                            {domainRecord?.verification_note && (
                                <SummaryRow label='Note'>
                                    <span className='sso_wrap_value'>{domainRecord.verification_note}</span>
                                </SummaryRow>
                            )}
                        </div>
                    ) : (
                        <>
                            <div className='sso_field sso_field_wide'>
                                <label className='sso_label'>Note</label>
                                <textarea
                                    className='sso_textarea'
                                    value={form.note}
                                    maxLength={500}
                                    placeholder='NMT-owned QA domain, approved for staging SSO testing'
                                    onChange={(e) => setField('note', e.target.value, 'verify')}
                                />
                            </div>

                            <Message message={msg.verify} />

                            <div className='sso_actions'>
                                <CommonButton
                                    text={<BtnLabel busy={busy.verify}>Verify Domain</BtnLabel>}
                                    disable={busy.verify}
                                    onClick={handleManualVerify}
                                />
                            </div>
                        </>
                    )}
                </StepCard>
            )}

            {/* ---------------- Step 4 ---------------- */}
            {step4Visible && (
                <StepCard
                    step={4}
                    done={finished}
                    title='Enable SSO'
                    description='Point the connection at the identity provider metadata URL so the certificate refreshes on its own, then switch SSO on. You can change this later from All SSO Domains.'
                >
                    <div className='sso_field sso_field_wide'>
                        <label className='sso_label'>IdP Metadata URL</label>
                        <CommonInput
                            name='metadataUrl'
                            value={form.metadataUrl}
                            onChange={(e) => setField('metadataUrl', e.target.value, 'enable')}
                            placeholder='https://login.microsoftonline.com/<tenant-id>/federationmetadata/2007-06/federationmetadata.xml'
                        />
                    </div>

                    <div className='sso_enable_row'>
                        <span className='sso_label'>SSO Enabled</span>
                        <span className='sso_toggle_cell'>
                            <div
                                className={`toggle-switch ${form.enabled ? 'on' : 'off'}`}
                                onClick={() => setField('enabled', !form.enabled, 'enable')}
                            >
                                <div className='toggle-thumb' />
                            </div>
                            <Body as='span' variant={2}>{form.enabled ? 'On' : 'Off'}</Body>
                        </span>
                    </div>

                    <Message message={msg.enable} />

                    <div className='sso_actions'>
                        <CommonButton
                            text={<BtnLabel busy={busy.enable}>Save &amp; Apply</BtnLabel>}
                            disable={busy.enable}
                            onClick={handleSaveConnection}
                        />
                        {finished && (
                            <>
                                <button type='button' className='sso_ghost_btn' onClick={startOver}>
                                    Set Up Another Domain
                                </button>
                                <button
                                    type='button'
                                    className='sso_ghost_btn'
                                    onClick={() => navigate('/ssoAuth/domains')}
                                >
                                    View All SSO Domains
                                </button>
                            </>
                        )}
                    </div>
                </StepCard>
            )}
        </div>
    );
}

export default SsoAuthentication;
